import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { siteData } from "@/store/siteData";
import { userStore } from "@/utils/userStore";
import useLogoUrl from "@/hooks/useLogoUrl";
import { PAGES } from "@/pages/kontrol-paneli/editors/registry";

type Device = "desktop" | "tablet" | "mobile";
const DEVICE_W: Record<Device, string> = { desktop: "100%", tablet: "834px", mobile: "390px" };
const DEVICE_ICON: Record<Device, string> = { desktop: "ri-computer-line", tablet: "ri-tablet-line", mobile: "ri-smartphone-line" };

export default function VisualEditor() {
  const navigate = useNavigate();
  const logoUrl = useLogoUrl();
  const [session] = useState(() => userStore.getSession());

  const [pageId, setPageId] = useState("home");
  const page = PAGES.find((p) => p.id === pageId) ?? PAGES[0];
  const [sectionId, setSectionId] = useState(page.sections[0].id);
  const section = page.sections.find((s) => s.id === sectionId) ?? page.sections[0];

  const [device, setDevice] = useState<Device>("desktop");
  const [reloadTick, setReloadTick] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const sendScroll = useCallback((sec: string) => {
    iframeRef.current?.contentWindow?.postMessage({ type: "cc-edit-scrollTo", section: sec }, "*");
  }, []);

  // Sayfa değişince ilk bölümü seç
  const switchPage = (pid: string) => {
    const p = PAGES.find((x) => x.id === pid) ?? PAGES[0];
    setPageId(pid);
    setSectionId(p.sections[0].id);
  };

  // Bölüm seçilince önizlemeyi kaydır
  const selectSection = (sid: string) => {
    setSectionId(sid);
    sendScroll(sid);
  };

  // iframe'den gelen "bölüm seçildi" mesajını dinle
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data;
      if (d?.type === "cc-edit-select" && d.section) {
        if (page.sections.some((s) => s.id === d.section)) setSectionId(d.section);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [page]);

  // Herhangi bir kayıttan sonra önizlemeyi tazele
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;
    const unsub = siteData.subscribe(() => {
      clearTimeout(t);
      t = setTimeout(() => setReloadTick((n) => n + 1), 200);
    });
    return () => { clearTimeout(t); unsub(); };
  }, []);

  if (!session) {
    return (
      <div className="min-h-screen bg-[#0A1020] grid place-items-center px-4">
        <div className="text-center">
          <i className="ri-lock-2-line text-[#3B82F6] text-4xl"></i>
          <h1 className="text-white text-lg font-bold mt-4">Görsel Editör</h1>
          <p className="text-[#7B8FAF] text-sm mt-1 mb-5">Bu sayfayı kullanmak için yönetim paneline giriş yapın.</p>
          <button onClick={() => navigate("/kontrol-paneli")} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-sm font-medium px-5 py-2.5 rounded-lg cursor-pointer">Panele Git</button>
        </div>
      </div>
    );
  }

  const src = `${page.path}?edit=1&t=${reloadTick}`;

  return (
    <div className="fixed inset-0 flex flex-col bg-[#0A1020]">
      {/* ── Üst Araç Çubuğu ─────────────────────────────────────────── */}
      <header className="h-14 flex items-center gap-3 px-3 sm:px-5 bg-[#0F1729] border-b border-[#2B3654] flex-shrink-0">
        <button onClick={() => navigate("/kontrol-paneli")} className="flex items-center gap-2 text-[#7B8FAF] hover:text-white text-sm cursor-pointer px-2 py-1.5 rounded-lg hover:bg-white/5">
          <i className="ri-arrow-left-line"></i><span className="hidden sm:inline">Panel</span>
        </button>
        <div className="h-6 w-px bg-[#2B3654]"></div>
        <img src={logoUrl} alt="" className="h-7 w-auto object-contain" />
        <span className="text-white text-sm font-semibold hidden md:inline">Görsel Editör</span>

        {/* Sayfa seçici */}
        <select value={pageId} onChange={(e) => switchPage(e.target.value)} className="ml-2 bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-1.5 outline-none focus:border-[#3B82F6] cursor-pointer">
          {PAGES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>

        {/* Cihaz seçimi */}
        <div className="mx-auto flex items-center gap-1 bg-[#0A1020] border border-[#2B3654] rounded-lg p-1">
          {(["desktop", "tablet", "mobile"] as Device[]).map((d) => (
            <button key={d} onClick={() => setDevice(d)} title={d} className={`w-8 h-7 grid place-items-center rounded cursor-pointer ${device === d ? "bg-[#3B82F6] text-white" : "text-[#7B8FAF] hover:text-white"}`}>
              <i className={DEVICE_ICON[d]}></i>
            </button>
          ))}
        </div>

        <button onClick={() => setReloadTick((n) => n + 1)} title="Önizlemeyi yenile" className="w-9 h-8 grid place-items-center text-[#7B8FAF] hover:text-white cursor-pointer rounded-lg hover:bg-white/5"><i className="ri-refresh-line"></i></button>
        <a href={page.path} target="_blank" rel="noreferrer" title="Siteyi yeni sekmede aç" className="w-9 h-8 grid place-items-center text-[#7B8FAF] hover:text-white cursor-pointer rounded-lg hover:bg-white/5"><i className="ri-external-link-line"></i></a>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* ── Sol: Bölüm listesi ──────────────────────────────────── */}
        <aside className="w-56 lg:w-64 flex-shrink-0 bg-[#0F1729] border-r border-[#2B3654] overflow-y-auto hidden md:block">
          <p className="px-4 pt-4 pb-2 text-[#5E7398] text-xs uppercase tracking-widest">{page.label} Bölümleri</p>
          <nav className="px-2 pb-4 space-y-0.5">
            {page.sections.map((s) => (
              <button key={s.id} onClick={() => selectSection(s.id)} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-left cursor-pointer transition-colors ${sectionId === s.id ? "bg-[#3B82F6]/20 text-white border border-[#3B82F6]/40" : "text-[#7B8FAF] hover:text-white hover:bg-white/5 border border-transparent"}`}>
                <i className={`${s.icon} text-base flex-shrink-0`}></i>
                <span className="flex-1 truncate">{s.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* ── Orta: Canlı önizleme ────────────────────────────────── */}
        <main className="flex-1 min-w-0 bg-[#070B14] overflow-auto p-3 sm:p-6">
          <div className="mx-auto bg-white rounded-lg overflow-hidden shadow-2xl transition-all duration-300" style={{ width: DEVICE_W[device], maxWidth: "100%", height: "100%" }}>
            <iframe
              key={page.path}
              ref={iframeRef}
              src={src}
              title="Önizleme"
              className="w-full h-full border-0 bg-white"
              onLoad={() => sendScroll(sectionId)}
            />
          </div>
        </main>

        {/* ── Sağ: Bölüm editörü ──────────────────────────────────── */}
        <aside className="w-80 lg:w-96 flex-shrink-0 bg-[#141E35] border-l border-[#2B3654] flex flex-col">
          <div className="h-12 flex items-center gap-2 px-4 border-b border-[#2B3654] flex-shrink-0">
            <i className={`${section.icon} text-[#3B82F6]`}></i>
            <span className="text-white text-sm font-semibold truncate">{section.label}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <section.Editor key={`${pageId}:${section.id}`} />
          </div>
        </aside>
      </div>
    </div>
  );
}
